/**
 * DAC profiles -- group 02 (professional-lineage and network-clocked designs).
 *
 * The spread here is deliberately about *where the distortion comes from*: two
 * multibit-delta-sigma implementations whose residual is modulator-limited, an
 * AKM part whose character is really a DSP layer, a discrete ladder whose error
 * is level-dependent INL, an FPGA design that converts everything to 1-bit, and
 * a dual-architecture unit that can be either. Published figures are tagged
 * MANUFACTURER_SPEC; anything inferred from topology is ESTIMATED and said so
 * in the provenance note. Nothing here is a measurement.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  makeDac,
  usd,
} from '../kit';

export const DACS_02: DacProfile[] = [
  makeDac({
    mfr: 'Audio-GD',
    model: 'R-7HE',
    price: usd(3000, 'APPROXIMATE', 2023, 'Audio-GD sells factory-direct and re-prices per production batch; roughly USD 3,000 for the R-7HE. This is not a published MSRP, hence APPROXIMATE.'),
    year: 2020,
    country: 'China',
    architecture: 'discrete-r2r',
    core: 'Discrete R-2R ladder, multiple resistor networks per channel under FPGA control',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 118,
    thdPlusNDb: -100,
    crosstalkDb: -108,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.nos(), FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.5,
    effectiveBits: 20,
    jitterPsRms: 55,
    confidence: 'ESTIMATED',
    accent: '#b98a5e',
    tags: ['nos', 'acss', 'fully-discrete', 'factory-direct'],
    notes:
      'Discrete ladder conversion feeding a fully discrete Class A output stage with no op-amps in the signal path and no global loop around the output. Two consequences dominate the model: in the default non-oversampling mode the zero-order hold imposes the sin(x)/x droop, and resistor trim error appears as level-dependent integral nonlinearity whose relative magnitude does not fall away at low signal level the way a modulator noise floor does. Output impedance is low for a ladder design because the I/V conversion is active rather than a passive resistor into the next input stage.',
    provenanceNote:
      'Architecture, discrete Class A output stage and the NOS/OS mode switch are from Audio-GD published product descriptions. Audio-GD does not publish a full THD+N or dynamic-range figure for this model, so both are ESTIMATED from class norms for discrete ladder designs at this price; ladder mismatch in LSB is an authored modelling estimate, not a measured or quoted value.',
  }),

  makeDac({
    mfr: 'RME',
    model: 'ADI-2 DAC FS',
    price: usd(1299, 'APPROXIMATE', 2024, 'Introduced near USD 1,099; US street price has since sat around USD 1,299-1,399. Figure APPROXIMATE because it has moved with revisions and exchange rates.'),
    year: 2017,
    country: 'Germany',
    architecture: 'delta-sigma',
    core: 'AKM AK4490/AK4493 delta-sigma with RME SteadyClock FS',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -116,
    crosstalkDb: -120,
    outputVrmsSE: 3.46,
    outputImpedanceOhms: 100,
    balanced: false,
    dsd: 'dsd256',
    filters: AKM_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 8,
    noiseShaperOrder: 5,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7fa6d8',
    tags: ['dsp-eq', 'crossfeed', 'steadyclock', 'unbalanced-out'],
    notes:
      'The analog stage is a conventional low-impedance op-amp output and is not where this design differs from its neighbours; the difference is a full DSP layer -- parametric EQ, loudness, crossfeed, reference-level switching -- running ahead of the modulator, so anything it does is exact and reproducible rather than a byproduct of the hardware. Distortion is modulator-limited rather than stage-limited, so the residual sits at a fixed low floor instead of growing with drive. Line output is single-ended only, with a 100 ohm source impedance that matters when feeding long cables or low-impedance inputs.',
    provenanceNote:
      'Dynamic range, THD+N, output impedance and switchable reference levels are RME published specifications. The AKM part number varies by production revision (AK4490 in early units, AK4493 later), so the core is stated as the pair. Filter set follows the AKM roll-off options RME exposes in the menu.',
  }),

  makeDac({
    mfr: 'Benchmark Media Systems',
    model: 'DAC3 HGC',
    price: usd(2195, 'ORIGINAL_RETAIL', 2017, 'US retail at introduction. Current retail is higher.'),
    year: 2017,
    country: 'United States',
    architecture: 'multibit-delta-sigma',
    core: 'ESS ES9028PRO with Benchmark UltraLock3 jitter attenuation',
    outputStage: 'op-amp',
    dynamicRangeDb: 128,
    thdPlusNDb: -113,
    crosstalkDb: -130,
    outputVrmsSE: 2.1,
    outputVrmsBal: 4.2,
    outputImpedanceOhms: 30,
    balanced: true,
    dsd: 'dsd128',
    filters: [
      { ...FILTERS.sharpLinear(128), id: 'bm-fixed', label: 'Fixed linear-phase reconstruction (not user selectable)' },
    ],
    jitterPsRms: 6,
    noiseShaperOrder: 6,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#63c9a8',
    tags: ['pro-lineage', 'fixed-filter', 'jitter-immune', 'passive-attenuator'],
    notes:
      'The conversion clock is fully decoupled from the input by an asynchronous upsampling stage, so incoming interface jitter is attenuated rather than tracked -- this profile therefore carries an unusually low jitter term and no periodic jitter component. Benchmark exposes no filter choice at all, which is a design position, not an omission: the single linear-phase reconstruction filter is modelled with fixed pre- and post-ringing. The ES9028PRO modulator is multi-bit rather than single-bit, so idle tones are low and the noise floor is comparatively flat instead of steeply shaped.',
    provenanceNote:
      'Dynamic range, THD+N, channel separation and the ES9028PRO core are Benchmark published specifications. Output level is jumper-selectable in the real unit; the nominal 2.1 V / 4.2 V calibration is used here. The multibit-delta-sigma architecture label reflects the multi-bit quantiser in the ESS HyperStream modulator and is a classification choice, not a Benchmark statement.',
  }),

  makeDac({
    mfr: 'Mytek',
    model: 'Manhattan II',
    price: usd(5995, 'APPROXIMATE', 2017, 'Approximately USD 5,995 at introduction; figure APPROXIMATE.'),
    year: 2017,
    country: 'United States',
    architecture: 'multibit-delta-sigma',
    core: 'ESS Sabre PRO-series eight-channel converter, all sections summed per channel',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 127,
    thdPlusNDb: -110,
    crosstalkDb: -125,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 75,
    balanced: true,
    dsd: 'dsd256',
    filters: ESS_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 20,
    noiseShaperOrder: 6,
    confidence: 'ESTIMATED',
    accent: '#e0664f',
    tags: ['studio-lineage', 'mqa', 'summed-outputs', 'analog-volume'],
    notes:
      'Summing all eight converter sections into two channels trades chip count for noise: uncorrelated modulator noise falls by roughly 6 dB per quadrupling of parallel sections while the signal adds coherently, which is why the dynamic range sits above what a single stereo section reaches. The output stage is discrete rather than an integrated op-amp, so its harmonic residue is low-order and mildly asymmetric rather than the flat high-order floor of a heavily fed-back monolithic stage. The full ESS filter menu is exposed, so filter choice, not the analog stage, is the biggest audible variable here.',
    provenanceNote:
      'Converter family, summed-output topology, filter menu and feature set are from Mytek published material; the exact ESS part number is not stated here because I am not confident which PRO-series device this revision uses. Dynamic range and THD+N are ESTIMATED from the architecture and are not quotations. Designed in the United States, manufactured in Poland.',
  }),

  makeDac({
    mfr: 'Weiss Engineering',
    model: 'DAC502',
    price: usd(9900, 'APPROXIMATE', 2023, 'Roughly USD 9,900; Weiss pricing varies by market and option set. APPROXIMATE.'),
    year: 2018,
    country: 'Switzerland',
    architecture: 'delta-sigma',
    core: 'Delta-sigma conversion stage fed by a Weiss FPGA DSP chain, multiple converter sections per channel',
    outputStage: 'op-amp',
    dynamicRangeDb: 125,
    thdPlusNDb: -110,
    crosstalkDb: -120,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'dsd128',
    filters: [FILTERS.sharpLinear(128), FILTERS.apodizing(128), FILTERS.slowMinimum(32)],
    jitterPsRms: 15,
    confidence: 'ESTIMATED',
    accent: '#9d8cd8',
    tags: ['dsp-chain', 'room-eq', 'crosstalk-cancellation', 'studio-lineage'],
    notes:
      'Everything that distinguishes this unit happens as DSP ahead of the modulator: room equalisation, a de-esser, a creative EQ and a crosstalk-cancellation stage all run at high internal word length before any conversion occurs, which means their effect is a pure, repeatable transfer function rather than an artefact of analog circuitry. The converter and output stage are deliberately unremarkable and contribute a low, flat, modulator-limited residual. Compared with the discrete designs in this group its distortion does not grow with level, so its fingerprint is essentially the filter choice plus whatever the DSP chain is set to do.',
    provenanceNote:
      'DSP feature set and signal chain from Weiss published material. Weiss does not publish a converter chipset for this model, so the core is described generically rather than guessed. Dynamic range, THD+N, crosstalk and the filter set are ESTIMATED from the class of the design and are not quoted figures.',
  }),

  makeDac({
    mfr: 'Merging Technologies',
    model: 'NADAC',
    price: usd(10500, 'APPROXIMATE', 2016, 'Approximately USD 10,500 for the stereo NADAC. APPROXIMATE.'),
    year: 2015,
    country: 'Switzerland',
    architecture: 'delta-sigma',
    core: 'Multi-channel delta-sigma conversion clocked inside the Merging RAVENNA/AES67 network domain',
    outputStage: 'op-amp',
    dynamicRangeDb: 124,
    thdPlusNDb: -112,
    crosstalkDb: -125,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 40,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [FILTERS.sharpLinear(128), FILTERS.apodizing(128)],
    jitterPsRms: 4,
    confidence: 'ESTIMATED',
    accent: '#4fb0c6',
    tags: ['ravenna', 'network-audio', 'clock-master', 'pro-lineage'],
    notes:
      'The differentiator is the transport, not the analog stage: audio arrives over Ethernet into a buffer that the converter clocks out of on its own oscillator, so there is no phase-locked recovery from an incoming S/PDIF or USB stream and no jitter path from the source at all. This profile therefore carries the lowest jitter term in the group by roughly an order of magnitude, with a random rather than periodic spectrum. Output levels are calibrated to pro-audio convention and the stage is DC-coupled, so there is no coupling-capacitor low-frequency corner to model.',
    provenanceNote:
      'Network transport, clocking architecture and channel count are from Merging Technologies published material. Dynamic range, THD+N, crosstalk and output impedance are ESTIMATED from the pro-audio class of the design; Merging publishes its converter figures in a measurement format this schema does not map directly, so nothing here is quoted. The jitter figure is a modelling estimate that reflects the buffered network clock, not a measurement.',
  }),

  makeDac({
    mfr: 'Playback Designs',
    model: 'MPD-6',
    price: usd(22000, 'APPROXIMATE', 2020, 'Approximately USD 22,000. APPROXIMATE.'),
    year: 2019,
    country: 'United States',
    architecture: 'fpga-custom',
    core: 'Proprietary FPGA algorithm upsampling every input to 1-bit DSD, followed by discrete conversion',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 118,
    thdPlusNDb: -96,
    crosstalkDb: -105,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd256',
    filters: [
      { ...FILTERS.slowMinimum(48), id: 'pd-fixed', label: 'Proprietary DSD upsampling, gentle roll-off (fixed)', cutoffRatio: 0.78, transitionRatio: 0.34, stopbandDb: -70 },
    ],
    internalOversampling: 128,
    jitterPsRms: 12,
    slewRateVus: 25,
    confidence: 'ESTIMATED',
    accent: '#d9b04a',
    tags: ['dsd-native', 'fpga', 'discrete-conversion', 'gentle-filter'],
    notes:
      'Every input, PCM included, is converted to a high-rate 1-bit stream in an FPGA and then reconstructed by a discrete analog stage rather than a converter chip. That has two modelled consequences: the reconstruction filter is gentle and minimum-phase-like, so there is almost no pre-ringing but measurable ultrasonic image energy remains, and the distortion floor is an order of magnitude above the chip-based designs here because a 1-bit discrete conversion trades static linearity for time-domain behaviour. The high internal rate is what pushes the requantisation noise out of band.',
    provenanceNote:
      'Conversion architecture and the upsample-to-DSD approach are from Playback Designs published descriptions. Playback Designs does not publish THD or dynamic-range specifications for this model, so both figures are ESTIMATED class norms for a discrete 1-bit conversion stage and must not be read as quotations. Filter shape is MODELLED from the design intent, not from published coefficients.',
  }),

  makeDac({
    mfr: 'Bricasti Design',
    model: 'M21',
    price: usd(16500, 'APPROXIMATE', 2021, 'Approximately USD 16,500; varies with configuration. APPROXIMATE.'),
    year: 2019,
    country: 'United States',
    architecture: 'hybrid',
    core: 'Selectable paths: Analog Devices delta-sigma, discrete R-2R ladder, and a 1-bit DSD direct route',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 122,
    thdPlusNDb: -105,
    crosstalkDb: -115,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.sharpMinimum(96), FILTERS.sharpLinear(128), FILTERS.nosCompensated()],
    ladderMismatchLsb: 0.35,
    harmonicShape: HARMONIC_SHAPES.r2r,
    nonlinear: { kind: 'r2r-inl', inlLsb: 0.35, amount: 0.5, asymmetry: 0.25, headroomDb: 8 },
    jitterPsRms: 15,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#8d97a8',
    tags: ['dual-architecture', 'ladder-path', 'pro-lineage', 'discrete-output'],
    notes:
      'Three conversion paths share one chassis and one output stage, which makes this the only entry in the group whose fingerprint changes with a front-panel mode rather than only with a filter. This profile models the ladder path, because that is the path with a signature: element mismatch produces a slowly decaying harmonic tail that persists at low level, whereas the delta-sigma path would show a lower and flatter floor. Filter selection remains oversampled by default, with a droop-compensated non-oversampling option that removes the sin(x)/x loss the raw hold would impose.',
    provenanceNote:
      'The multi-architecture design and the selectable conversion modes are from Bricasti published material; the delta-sigma part number is not stated here because I am not confident of it. Dynamic range, THD+N and ladder mismatch are ESTIMATED, not quoted -- Bricasti publishes little numerical specification for this model. The choice to model the ladder path rather than the delta-sigma path is an authoring decision recorded in the notes.',
  }),
];
